import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/common/Logo/Logo';
import pageStyles from './SignupPage.module.css';
import { useAuth } from '../../contexts/AuthContext';
import TermsModal from '../../shared/terms/TermsModal';
import ServiceTerms from '../../shared/terms/ServiceTerms';
import PrivacyPolicy from '../../shared/terms/PrivacyPolicy';
import MarketingConsent from '../../shared/terms/MarketingConsent';
import AiTrainingConsent from '../../shared/terms/AiTrainingConsent';
import { sendPhoneCode, verifyPhoneLogin, signup, login as apiLogin, checkEmail } from '../../api';
import { checkPhone, isManagedExisting } from '../../api/authApi';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

const inputStyle = {
  boxSizing: 'border-box',
  padding: '0px 18px',
  width: '100%',
  height: '60px',
  background: '#FCFCFD',
  border: '1px solid #E9E9EC',
  borderRadius: '10px',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  letterSpacing: '-0.04em',
  color: '#090909',
  outline: 'none',
};

const labelStyle = {
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 500,
  fontSize: '18px',
  letterSpacing: '-0.04em',
  color: '#090909',
};

const sideBtn = {
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0px 16px',
  whiteSpace: 'nowrap',
  height: '60px',
  background: '#FFFFFF',
  border: '1px solid #E9E9EC',
  borderRadius: '10px',
  cursor: 'pointer',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  letterSpacing: '-0.04em',
  color: '#555555',
  flexShrink: 0,
};

function CheckIcon({ checked }) {
  return (
    <div style={{
      width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0,
      border: `2px solid ${checked ? '#090909' : '#E9E9EC'}`,
      background: checked ? '#090909' : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {checked && (
        <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
          <path d="M1 5l3.5 3.5L12 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed', top: '32px', left: '50%', transform: 'translateX(-50%)',
      background: '#090909', color: '#FFF', padding: '14px 28px',
      borderRadius: '12px', fontFamily: 'Pretendard, sans-serif', fontSize: '16px',
      fontWeight: 500, letterSpacing: '-0.04em', zIndex: 2000,
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    }}>
      {message}
    </div>
  );
}

function StepOne({ role, setRole, onNext, navigate }) {
  return (
    <div className={pageStyles.stepOneCard} style={{
      boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
      alignItems: 'flex-start', gap: '32px',
    }}>
      <Logo onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '30px', letterSpacing: '-0.04em', color: '#090909' }}>회원가입</span>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>어떤 역할로 가입하시겠어요?</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
        {[
          { key: 'talent', title: '배우 / 모델', desc: '공고에 지원하고 캐스팅 기회를 찾습니다.',
            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#A2A2A2" strokeWidth="2"/><path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="#A2A2A2" strokeWidth="2" strokeLinecap="round"/></svg> },
          { key: 'producer', title: '제작사 / PD', desc: '공고를 등록하고 배우를 캐스팅합니다.',
            icon: <svg width="28" height="24" viewBox="0 0 28 24" fill="none"><rect x="2" y="3" width="24" height="17" rx="2" stroke="#A2A2A2" strokeWidth="2"/><path d="M9 20v2M19 20v2M7 22h14" stroke="#A2A2A2" strokeWidth="2" strokeLinecap="round"/></svg> },
        ].map(({ key, title, desc, icon }) => (
          <div key={key} onClick={() => setRole(key)} style={{
            boxSizing: 'border-box', display: 'flex', flexDirection: 'row',
            justifyContent: 'space-between', alignItems: 'center',
            padding: '20px 22px', gap: '30px', width: '100%', height: '90px',
            background: role === key ? '#FFF' : '#FCFCFD',
            border: `1px solid ${role === key ? '#090909' : '#E9E9EC'}`,
            boxShadow: role === key ? '2px 2px 10px rgba(0,0,0,0.08)' : 'none',
            borderRadius: '10px', cursor: 'pointer',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ width: '50px', height: '50px', background: '#E9E9EC', borderRadius: '1000px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '20px', letterSpacing: '-0.04em', color: '#000' }}>{title}</span>
                <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>{desc}</span>
              </div>
            </div>
            <CheckIcon checked={role === key} />
          </div>
        ))}
      </div>

      <button onClick={onNext} style={{
        width: '100%', height: '60px', background: '#090909', borderRadius: '10px', border: 'none',
        cursor: 'pointer', fontFamily: 'Pretendard, sans-serif', fontWeight: 600,
        fontSize: '20px', letterSpacing: '-0.04em', color: '#FFF',
      }}>
        다음단계 : 기본 정보 입력
      </button>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', width: '100%' }}>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>이미 계정이 있으신가요?</span>
        <span onClick={() => navigate('/login')} style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', textDecoration: 'underline', color: '#090909', cursor: 'pointer' }}>로그인하기</span>
      </div>
    </div>
  );
}

function StepTwo({ role, onBack, navigate, onComplete, isSubmitting }) {
  const [email, setEmail] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailCheckMsg, setEmailCheckMsg] = useState('');
  // 이 이메일로 이미 대행 계정이 발급돼 있는 경우 — 가입이 아니라 전환으로 안내한다
  const [managedAccount, setManagedAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [checks, setChecks] = useState({ terms: false, privacy: false, marketing: false, aiTraining: false });
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [timer, setTimer] = useState(180);
  const [verified, setVerified] = useState(false);
  const [toast, setToast] = useState(null);
  const intervalRef = useRef(null);

  const roleLabel = role === 'talent' ? '배우 / 모델' : '제작자';

  const allChecked = checks.terms && checks.privacy && checks.marketing && checks.aiTraining;
  const handleCheckAll = () => {
    const next = !allChecked;
    setChecks({ terms: next, privacy: next, marketing: next, aiTraining: next });
  };

  const startTimer = () => {
    clearInterval(intervalRef.current);
    setTimer(180);
    intervalRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) { clearInterval(intervalRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const handleCheckEmail = async () => {
    if (!email.trim()) { setToast('이메일을 입력해주세요.'); return; }
    const emailRegex = /^([0-9a-zA-Z_.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/;
    if (!emailRegex.test(email.trim())) {
      setEmailChecked(false);
      setEmailCheckMsg('올바른 이메일 형식이 아닙니다.');
      return;
    }
    // 응답을 받기 전에 '사용 가능'으로 표시하면 확인 전에 가입을 눌러버릴 수 있다
    setEmailChecked(false);
    setEmailCheckMsg('확인 중...');
    setManagedAccount(false);
    try {
      const res = await checkEmail(email.trim());
      if (!res?.exists) {
        setEmailChecked(true);
        setEmailCheckMsg('사용 가능한 이메일입니다.');
        return;
      }
      setEmailChecked(false);
      // 대행 계정이면 새로 가입하지 말고 그 계정을 전환해야 한다.
      // 다른 이메일로 새로 만들면 기존 공고·지원자가 옛 계정에 남는다.
      if (isManagedExisting(res)) {
        setManagedAccount(true);
        setEmailCheckMsg('이미 발급된 대행 계정이 있는 이메일입니다.');
      } else {
        setEmailCheckMsg('이미 사용 중인 이메일입니다.');
      }
    } catch {
      setEmailChecked(false);
      setEmailCheckMsg('중복 확인에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleSendCode = async () => {
    if (!phone.trim()) { setToast('휴대폰 번호를 입력해주세요.'); return; }
    const phoneRegex = /^01[016789][0-9]{7,8}$/;
    if (!phoneRegex.test(phone.replace(/-/g, ''))) {
      setToast('올바른 휴대폰 번호 형식이 아닙니다.');
      return;
    }
    try {
      // 1단계: 전화번호 중복 확인
      const dupRes = await checkPhone(phone.trim());
      if (dupRes?.exists) {
        setToast('이미 가입된 전화번호입니다.');
        return;
      }
      // 2단계: SMS 인증 요청
      await sendPhoneCode(phone.trim());
      setCodeSent(true);
      setVerified(false);
      setCode('');
      startTimer();
      setToast('인증번호가 발송되었습니다.');
    } catch {
      setToast('인증번호 발송에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleVerify = async () => {
    try {
      const res = await verifyPhoneLogin(phone, code);
      if (res.verified) {
        clearInterval(intervalRef.current);
        setVerified(true);
        setToast('인증되었습니다.');
      } else {
        const msg = {
          WRONG_CODE: '인증번호가 올바르지 않습니다.',
          EXPIRED: '인증번호가 만료되었습니다.',
          LOCKED: '시도 횟수를 초과했습니다.',
        }[res.errorCode] || '인증에 실패했습니다.';
        setToast(msg);
      }
    } catch {
      setToast('인증 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const TERMS = [
    { key: 'terms', label: '서비스 이용약관에 동의합니다.', req: true, title: '서비스 이용약관', content: ServiceTerms },
    { key: 'privacy', label: '개인정보처리방침에 동의합니다.', req: true, title: '개인정보처리방침', content: PrivacyPolicy },
    { key: 'marketing', label: '개인정보 수집 & 이용 및 제3자 제공에 동의합니다.', req: false, title: '개인정보 수집 & 이용 및 제3자 제공 동의', content: MarketingConsent },
    { key: 'aiTraining', label: '개인정보의 AI 학습,모델 고도화에 동의합니다.', req: false, title: '개인정보의 AI 학습, 모델 고도화 동의', content: AiTrainingConsent },
  ];

  const [termsModal, setTermsModal] = useState(null);

  return (
    <div className={pageStyles.stepTwoCard} style={{
      boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
      alignItems: 'flex-start', gap: '24px',
    }}>
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}

      <Logo onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 14px', background: '#E9E9EC', borderRadius: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '20px', letterSpacing: '-0.04em', color: '#090909' }}>{roleLabel}</span>
        </div>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '28px', letterSpacing: '-0.04em', color: '#090909' }}>기본 정보 입력</span>
      </div>

      {/* 휴대폰 번호 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <span style={labelStyle}>휴대폰 번호</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input
            type="tel" placeholder="010-0000-0000"
            value={phone} onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
              const formatted = digits.length >= 8
                ? `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`
                : digits.length >= 4
                ? `${digits.slice(0,3)}-${digits.slice(3)}`
                : digits;
              setPhone(formatted);
            }}
            style={{ ...inputStyle, flex: 1 }}
            disabled={verified}
          />
          <button onClick={handleSendCode} disabled={verified || !phone} style={{
            ...sideBtn,
            background: verified ? '#F0F0F0' : '#FFF',
            color: verified ? '#A2A2A2' : '#555',
            cursor: verified ? 'default' : 'pointer',
          }}>
            {codeSent && !verified ? '재발송' : '인증요청'}
          </button>
        </div>

        {codeSent && !verified && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text" placeholder="인증번호 입력"
                value={code} onChange={(e) => setCode(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={handleVerify} disabled={!code} style={{
                ...sideBtn,
                background: code ? '#090909' : '#E9E9EC',
                color: code ? '#FFF' : '#A2A2A2',
                cursor: code ? 'pointer' : 'default',
              }}>
                인증완료
              </button>
            </div>
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '14px', color: timer > 0 ? '#FF4343' : '#A2A2A2', textAlign: 'right', letterSpacing: '-0.04em' }}>
              {timer > 0 ? `${fmt(timer)} 남음` : '시간이 만료되었습니다'}
            </span>
          </div>
        )}

        {verified && (
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '14px', color: '#22C55E', letterSpacing: '-0.04em' }}>✓ 인증이 완료되었습니다.</span>
        )}
      </div>


      {/* 약관 */}
      <div style={{ width: '100%', height: '1px', background: '#E9E9EC' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span style={labelStyle}>이용약관 동의</span>
        <div onClick={handleCheckAll}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 14px', background: '#F5F5F7', borderRadius: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.04em', color: '#090909' }}>
            전체 동의
          </span>
          <CheckIcon checked={allChecked} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
        {TERMS.map(({ key, label, req, title, content }) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '14px' }}>
            <span
              onClick={(e) => { e.stopPropagation(); setTermsModal({ title, content }); }}
              style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909', cursor: 'pointer' }}>
              <span style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>{label}</span>{req && <span style={{ color: '#FF9900', marginLeft: '4px' }}>*</span>}
            </span>
            <div onClick={() => setChecks((p) => ({ ...p, [key]: !p[key] }))} style={{ cursor: 'pointer' }}>
              <CheckIcon checked={checks[key]} />
            </div>
          </div>
        ))}
      </div>

      {/* 계정 정보 입력 */}
      <div style={{ width: '100%', height: '1px', background: '#E9E9EC' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <span style={{ ...labelStyle, fontWeight: 600, fontSize: '20px' }}>계정 정보 입력</span>
      </div>

      {/* 이메일 + 중복확인 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <span style={labelStyle}>이메일</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input
            type="email" placeholder="이메일 입력"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailChecked(false); setEmailCheckMsg(''); }}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={handleCheckEmail} style={{ ...sideBtn, background: emailChecked ? '#F0F0F0' : '#FFF' }}>
            중복확인
          </button>
        </div>
        {emailCheckMsg && (
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: emailChecked ? '#22C55E' : '#FF4343', letterSpacing: '-0.04em' }}>{emailCheckMsg}</span>
        )}
        {managedAccount && (
          <div style={{
            border: '1px solid #E9E9EC', borderRadius: '10px', padding: '14px 16px',
            fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#4B4B4B',
            lineHeight: 1.7, letterSpacing: '-0.04em',
          }}>
            캐스타에서 발급해드린 계정입니다. <b>새로 가입하지 마시고</b> 그 계정으로 로그인한 뒤
            설정에서 정식 계정으로 전환해주세요.<br />
            새 계정을 만들면 기존 공고와 지원자가 이전 계정에 남습니다.
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                display: 'block', marginTop: '10px', padding: '10px 18px',
                borderRadius: '8px', border: '1px solid #090909', background: '#fff',
                fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '13px',
                letterSpacing: '-0.04em', cursor: 'pointer',
              }}
            >
              로그인하러 가기
            </button>
          </div>
        )}
      </div>

      {/* 비밀번호 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <span style={labelStyle}>비밀번호</span>
        <div style={{ position: 'relative' }}>
          <input type={showPw ? 'text' : 'password'} placeholder="비밀번호 입력" value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, paddingRight: '50px' }} />
          <button onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {showPw ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <ellipse cx="10" cy="10" rx="8" ry="5" stroke="#A2A2A2" strokeWidth="1.5"/>
                <circle cx="10" cy="10" r="2" fill="#A2A2A2"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M2 2l16 16" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M7.5 4.5A8.5 8.5 0 0110 4c4.5 0 7.5 6 7.5 6s-1 2-3 3.5" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M4 6.5C3 7.5 2.5 9 2.5 10s3 6 7.5 6a8 8 0 003-0.6" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* 비밀번호 확인 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <span style={labelStyle}>비밀번호 확인</span>
        <div style={{ position: 'relative' }}>
          <input type={showPwConfirm ? 'text' : 'password'} placeholder="비밀번호 재입력" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} style={{ ...inputStyle, paddingRight: '50px' }} />
          <button onClick={() => setShowPwConfirm(!showPwConfirm)} style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {showPwConfirm ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <ellipse cx="10" cy="10" rx="8" ry="5" stroke="#A2A2A2" strokeWidth="1.5"/>
                <circle cx="10" cy="10" r="2" fill="#A2A2A2"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M2 2l16 16" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M7.5 4.5A8.5 8.5 0 0110 4c4.5 0 7.5 6 7.5 6s-1 2-3 3.5" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M4 6.5C3 7.5 2.5 9 2.5 10s3 6 7.5 6a8 8 0 003-0.6" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
        {password && passwordConfirm && password !== passwordConfirm && (
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#FF4343', letterSpacing: '-0.04em' }}>비밀번호가 일치하지 않습니다.</span>
        )}
      </div>

      {/* 버튼 */}
      <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
        <button onClick={onBack} style={{
          flex: 1, height: '60px', background: '#E9E9EC', borderRadius: '10px', border: 'none',
          cursor: 'pointer', fontFamily: 'Pretendard, sans-serif', fontWeight: 600,
          fontSize: '20px', letterSpacing: '-0.04em', color: '#A2A2A2',
        }}>이전으로</button>
        <button
          disabled={isSubmitting}
          onClick={() => {
            if (!email.trim() || !password.trim()) { setToast('이메일과 비밀번호를 입력해주세요.'); return; }
            if (!emailChecked) { setToast('이메일 중복 확인을 완료해주세요.'); return; }
            if (password !== passwordConfirm) { setToast('비밀번호가 일치하지 않습니다.'); return; }
            if (!verified) { setToast('휴대폰 인증을 완료해주세요.'); return; }
            if (!checks.terms || !checks.privacy) { setToast('필수 약관에 동의해주세요.'); return; }
            onComplete({ email, password, phone, consents: checks });
          }}
          style={{
            flex: 1, height: '60px',
            background: isSubmitting ? '#E9E9EC' : 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)',
            borderRadius: '10px', border: 'none', cursor: isSubmitting ? 'default' : 'pointer',
            fontFamily: 'Pretendard, sans-serif', fontWeight: 600,
            fontSize: '20px', letterSpacing: '-0.04em', color: '#090909',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
        >
          {isSubmitting && <LoadingSpinner size="sm" style={{ width: 22, height: 22 }} />}
          {isSubmitting ? '처리 중...' : '가입 완료'}
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', width: '100%' }}>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>이미 계정이 있으신가요?</span>
        <span onClick={() => navigate('/login')} style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', textDecoration: 'underline', color: '#090909', cursor: 'pointer' }}>로그인하기</span>
      </div>

      {termsModal && (
        <TermsModal
          title={termsModal.title}
          content={termsModal.content}
          onClose={() => setTermsModal(null)}
        />
      )}
    </div>
  );
}

function SignupPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('talent');
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * 가입 실패 안내. 서버가 200에 success:false로 알려줄 수도, 오류로 던질 수도 있어
   * 두 경로가 같은 곳으로 모이게 한다.
   */
  const showSignupFailure = (body) => {
    if (isManagedExisting(body)) {
      alert(
        '이 이메일로 이미 대행 계정이 발급돼 있습니다.\n'
        + '새로 가입하지 마시고 그 계정으로 로그인한 뒤, 설정에서 정식 계정으로 전환해주세요.',
      );
      navigate('/login');
      return;
    }
    alert(body?.message || '회원가입에 실패했습니다. 이메일 또는 전화번호를 확인해주세요.');
  };

  const handleComplete = async ({ email, password, phone, consents }) => {
    const isTalentRole = role === 'talent';
    const isProducerRole = role === 'producer';
    setIsSubmitting(true);
    try {
      const result = await signup({
        email, password, phone,
        isTalent: isTalentRole,
        isProducer: isProducerRole,
        termsAgreed: consents.terms,
        privacyAgreed: consents.privacy,
        marketingAgreed: consents.marketing,
        aiTrainingAgreed: consents.aiTraining,
      });
      // 실패를 오류로 던지지 않고 본문으로 알려주는 경우가 있다
      if (result?.success === false) {
        showSignupFailure(result);
        return;
      }

      await apiLogin(email, password);
      login({
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
        isTalent: isTalentRole,
        isProducer: isProducerRole,
      });
      navigate(isTalentRole ? '/onboarding/talent' : '/onboarding/producer');
    } catch (err) {
      // 중복 확인을 건너뛰었거나 그 사이에 발급된 경우를 여기서도 잡는다
      showSignupFailure(err.response?.data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={pageStyles.pageWrapper}>
      <div className={pageStyles.background} style={{ backgroundImage: 'url(/assets/images/login-curtain.jpg)' }} />
      {step === 1
        ? <StepOne role={role} setRole={setRole} onNext={() => setStep(2)} navigate={navigate} />
        : <StepTwo role={role} onBack={() => setStep(1)} navigate={navigate} onComplete={handleComplete} isSubmitting={isSubmitting} />}
    </div>
  );
}

export default SignupPage;
